import { inject, Injectable } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, timeout } from 'rxjs';
import { environment } from '@environment/environment';
import { HttpClient } from '@angular/common/http';
import { StartupTraceUtil } from '@shared/utils/startup-trace.util';

export interface BackendHealthStatus {
  name: string;
  status: 'Healthy' | 'DOWN';
  url: string;
}

export interface HealthResponse {
  overall: 'Healthy' | 'DOWN';
  backends: BackendHealthStatus[];
}

@Injectable({
  providedIn: 'root'
})
export class HealthService {
  private readonly http = inject(HttpClient);

  /**
   * LoadingService re-probes with exhaustMap, so a probe that never answers would swallow every
   * later retry. CapacitorHttp sets no read timeout on Android, so the bound has to live here.
   */
  private static readonly PROBE_TIMEOUT_MS = 20_000;

  /**
   * Opens a connection to the file backend so the first document download does not pay for a
   * cold DNS/TCP/TLS handshake — nothing else in the app talks to bppFile until a file is opened.
   *
   * Deliberately not part of {@link getHealth}: that result gates startup routing, and an
   * unhealthy file backend must not strand the whole app on the no-connection screen.
   * Fire and forget — the retry in DocumentsService still covers the real download.
   */
  public warmUpFileBackend(): void {
    this.http.get(`${ environment.bppFile }/health`, { responseType: 'text' }).pipe(
      catchError(() => of(null))
    ).subscribe();
  }

  public getHealth(): Observable<HealthResponse> {
    StartupTraceUtil.mark('health-check-start');
    const backends = [
      { name: 'Auth', url: environment.bppAuth },
      { name: 'Stella', url: environment.bppStella }
    ];

    const healthChecks = backends.map(backend =>
      this.http.get(`${backend.url}/health`, { responseType: 'text' }).pipe(
        timeout(HealthService.PROBE_TIMEOUT_MS),
        map((response) => {
          const isHealthy = response.trim().toLowerCase() === 'healthy';

          return {
            name: backend.name,
            status: isHealthy ? 'Healthy' as const : 'DOWN' as const,
            url: backend.url
          };
        }),
        catchError(() => {
          return of({
            name: backend.name,
            status: 'DOWN' as const,
            url: backend.url
          });
        })
      )
    );

    return forkJoin(healthChecks).pipe(
      map(results => {
        const overallStatus = results.every(r => r.status === 'Healthy') ? 'Healthy' : 'DOWN';
        StartupTraceUtil.mark('health-check-done', overallStatus);
        return {
          overall: overallStatus,
          backends: results
        };
      })
    );
  }
}
